// frontend/src/components/admin/Conformite.jsx
// ============================================================================
// CONFORMITE
// ----------------------------------------------------------------------------
// Treize controles qui disent ce qui devrait etre vrai et ne l'est pas. Les
// controles conformes se replient : ce qui compte, c'est ce qui cloche.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardCheck, RefreshCw, AlertOctagon, AlertTriangle,
  Info, CheckCircle2, ChevronDown, ChevronRight, Bug
} from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import { useTheme } from '../../contexts/ThemeContext';

const GRAVITES = {
  bloquant:    { libelle: 'À traiter',  Icon: AlertOctagon,  ton: 'danger' },
  attention:   { libelle: 'À surveiller', Icon: AlertTriangle, ton: 'warning' },
  information: { libelle: 'Pour information', Icon: Info,     ton: 'neutre' }
};

const couleurs = (t, ton) => ({
  danger:  { fond: t.dangerSoft,  texte: t.dangerText,  trait: t.danger },
  warning: { fond: t.warningSoft, texte: t.warningText, trait: t.warning },
  neutre:  { fond: t.bgInput,     texte: t.textSecondary, trait: t.border }
}[ton]);

const Carte = ({ Icon, libelle, valeur, ton, t }) => {
  const c = couleurs(t, ton);
  return (
    <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14,
      padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ width: 42, height: 42, borderRadius: 12, background: c.fond,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} style={{ color: c.texte }} />
      </div>
      <div>
        <p style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, margin: 0,
          textTransform: 'uppercase', letterSpacing: '0.5px' }}>{libelle}</p>
        <p style={{ fontSize: 22, fontWeight: 800, color: t.text, margin: 0, lineHeight: 1.2 }}>{valeur}</p>
      </div>
    </div>
  );
};

const Controle = ({ controle, t, ouvertParDefaut }) => {
  const [ouvert, setOuvert] = useState(ouvertParDefaut);
  const g = GRAVITES[controle.gravite] || GRAVITES.information;
  const conforme = controle.anomalies === 0 && !controle.erreur;
  const c = couleurs(t, controle.erreur ? 'danger' : conforme ? 'neutre' : g.ton);
  const Icone = controle.erreur ? Bug : conforme ? CheckCircle2 : g.Icon;
  const Chevron = ouvert ? ChevronDown : ChevronRight;

  return (
    <div style={{ background: t.bgCard, border: `1px solid ${conforme ? t.border : c.trait + '55'}`,
      borderRadius: 12, overflow: 'hidden' }}>
      <button type="button" onClick={() => setOuvert((o) => !o)}
        style={{ width: '100%', background: 'transparent', border: 0, cursor: 'pointer',
          padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 12,
          textAlign: 'left', fontFamily: 'inherit' }}>
        <Chevron size={15} style={{ color: t.textMuted, flexShrink: 0 }} />
        <Icone size={16} style={{ color: conforme ? t.successText : c.texte, flexShrink: 0 }} />
        <span style={{ flexGrow: 1, fontSize: 14, fontWeight: 600, color: t.text }}>
          {controle.libelle}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 99,
          background: conforme ? t.successSoft : c.fond,
          color: conforme ? t.successText : c.texte, whiteSpace: 'nowrap' }}>
          {controle.erreur ? 'contrôle en panne'
            : conforme ? 'conforme'
            : `${controle.anomalies} à voir`}
        </span>
      </button>

      {ouvert && (
        <div style={{ padding: '0 16px 14px 58px' }}>
          <p style={{ margin: '0 0 10px', fontSize: 13, color: t.textSecondary, lineHeight: 1.6 }}>
            {controle.explication}
          </p>

          {controle.erreur && (
            <p style={{ margin: 0, fontSize: 13, color: t.dangerText }}>
              Le contrôle n&rsquo;a pas pu s&rsquo;exécuter : {controle.erreur}
            </p>
          )}

          {!controle.erreur && conforme && (
            <p style={{ margin: 0, fontSize: 13, color: t.successText }}>Rien à signaler.</p>
          )}

          {!controle.erreur && !conforme && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {controle.lignes.map((l) => (
                <div key={`${controle.cle}-${l.id}`}
                  style={{ display: 'flex', gap: 14, padding: '8px 0',
                    borderTop: `1px solid ${t.borderLight}` }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: t.text, minWidth: 220 }}>
                    {l.libelle}
                  </span>
                  <span style={{ fontSize: 13, color: t.textMuted }}>{l.detail}</span>
                </div>
              ))}
              {controle.tronque && (
                <p style={{ margin: '8px 0 0', fontSize: 12, color: t.textMuted }}>
                  Seules les 25 premières lignes sont affichées.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Conformite = () => {
  const t = useTheme();
  const [etat, setEtat] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const r = await superAdminApi.conformite.etat();
      setEtat(r.data || null);
    } catch (e) {
      setErreur(e.message || 'Contrôles impossibles');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const resume = etat?.resume;
  const aProbleme = (etat?.controles || []).filter((c) => c.erreur || c.anomalies > 0);
  const conformes = (etat?.controles || []).filter((c) => !c.erreur && c.anomalies === 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: t.text, margin: 0,
            display: 'flex', alignItems: 'center', gap: 9 }}>
            <ClipboardCheck size={20} style={{ color: t.primary }} />
            Conformité
          </h2>
          <p style={{ fontSize: 13, color: t.textMuted, margin: '4px 0 0' }}>
            {resume
              ? `${resume.controles} contrôles, ${resume.conformes} conformes`
              : 'Exécution des contrôles…'}
          </p>
        </div>
        <button type="button" onClick={charger}
          style={{ background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 10,
            padding: '9px 14px', fontSize: 13, fontWeight: 600, color: t.text, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'inherit' }}>
          <RefreshCw size={14} style={{ animation: chargement ? 'spin 1s linear infinite' : 'none' }} />
          Relancer
        </button>
      </div>

      {erreur && (
        <div style={{ background: t.dangerSoft, border: `1px solid ${t.danger}30`, borderRadius: 14,
          padding: '14px 18px', fontSize: 14, color: t.dangerText }}>{erreur}</div>
      )}

      {resume && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
          <Carte Icon={AlertOctagon} libelle="À traiter" valeur={resume.bloquant} ton="danger" t={t} />
          <Carte Icon={AlertTriangle} libelle="À surveiller" valeur={resume.attention} ton="warning" t={t} />
          <Carte Icon={Info} libelle="Pour information" valeur={resume.information} ton="neutre" t={t} />
          <Carte Icon={CheckCircle2} libelle="Contrôles conformes"
            valeur={`${resume.conformes}/${resume.controles}`} ton="neutre" t={t} />
        </div>
      )}

      {etat && aProbleme.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {aProbleme.map((c) => (
            <Controle key={c.cle} controle={c} t={t} ouvertParDefaut={c.gravite === 'bloquant' || !!c.erreur} />
          ))}
        </div>
      )}

      {etat && aProbleme.length === 0 && !chargement && (
        <div style={{ background: t.successSoft, border: `1px solid ${t.success}30`, borderRadius: 14,
          padding: '16px 20px', fontSize: 14, color: t.successText }}>
          Les {resume.controles} contrôles passent. Rien à traiter.
        </div>
      )}

      {etat && conformes.length > 0 && (
        <div>
          <p style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, margin: '4px 0 10px',
            textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Contrôles conformes
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {conformes.map((c) => <Controle key={c.cle} controle={c} t={t} ouvertParDefaut={false} />)}
          </div>
        </div>
      )}

      <p style={{ fontSize: 12, color: t.textMuted, margin: 0, lineHeight: 1.6 }}>
        Les contrôles sont en lecture seule : ils signalent, ils ne corrigent rien.
        Un contrôle qui tombe en panne le dit et laisse les autres répondre.
      </p>
    </div>
  );
};

export default Conformite;
